import React, { useState, useEffect } from 'react';
import { getSecurityInsightsData, scheduleOutsideInScan } from '../../services/api';
import { trackGoogleAdsConversion } from '../../lib/googleAds';
import '../../styles/dashboard-pages.css';

const formatScanTimestamp = (timestamp) => {
  if (!timestamp) return null;
  const parsed = new Date(timestamp);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toLocaleString();
};

const parseScanPayload = (payload) => {
  if (!payload) return null;
  if (typeof payload === 'object') return payload;
  if (typeof payload !== 'string') return null;

  try {
    return JSON.parse(payload);
  } catch (_err) {
    return null;
  }
};

const toTitleCase = (value) => {
  if (!value) return 'Unknown';
  return String(value)
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const formatPortList = (ports) => {
  if (!Array.isArray(ports) || ports.length === 0) return 'None';
  return ports.join(', ');
};

const getExternalIntegrityImprovements = (scan) => {
  if (!scan) return [];

  const improvements = [];
  const tlsVersion = (scan.tls_version || '').toUpperCase();
  const websiteScan = scan.scan_payload?.website || null;
  const headerChecks = websiteScan?.headers || null;
  const headerChecksAvailable = Boolean(
    headerChecks &&
    typeof headerChecks === 'object' &&
    Object.keys(headerChecks).length > 0
  );

  if (scan.website_encryption === false) {
    improvements.push({
      title: 'Website encryption checks failed',
      recommendation: 'Review HTTPS enforcement and certificate setup so website encryption signals pass consistently.',
    });
  }

  if (scan.tls_valid === false) {
    improvements.push({
      title: 'TLS configuration is invalid',
      recommendation: 'Fix certificate validity issues and ensure your endpoint presents a valid TLS chain.',
    });
  }

  if (headerChecksAvailable && scan.secure_headers === false) {
    improvements.push({
      title: 'Missing secure HTTP headers',
      recommendation: 'Enable security headers such as Content-Security-Policy and related hardening headers.',
    });
  }

  if (typeof scan.open_ports_count === 'number' && scan.open_ports_count > 0) {
    improvements.push({
      title: `${scan.open_ports_count} open port${scan.open_ports_count > 1 ? 's are' : ' is'} publicly exposed`,
      recommendation: 'Restrict unnecessary internet-facing services to reduce attack surface.',
    });
  }

  if (scan.spf_valid === false) {
    improvements.push({
      title: 'SPF record is missing or invalid',
      recommendation: 'Publish and validate SPF records to improve email authenticity protection.',
    });
  }

  if (scan.dmarc_valid === false) {
    improvements.push({
      title: 'DMARC policy is missing or invalid',
      recommendation: 'Configure DMARC with enforcement to reduce spoofing and phishing risk.',
    });
  }

  if (scan.dnssec_enabled === false) {
    improvements.push({
      title: 'DNSSEC is not enabled',
      recommendation: 'Enable DNSSEC signing for your domain zone to strengthen DNS integrity.',
    });
  }

  if (tlsVersion.startsWith('TLS1.0') || tlsVersion.startsWith('TLS1.1')) {
    improvements.push({
      title: `Outdated TLS version detected (${scan.tls_version})`,
      recommendation: 'Move endpoints to TLS 1.2+ and disable legacy protocol support.',
    });
  }

  if ((scan.cve_match_count ?? 0) > 0 || (scan.cve_penalty ?? 0) > 0) {
    const count = scan.cve_match_count ?? 0;
    improvements.push({
      title: count > 0 ? `${count} CVE match${count > 1 ? 'es' : ''} detected` : 'CVE risk signal detected',
      recommendation: 'Prioritize patching and component upgrades for packages tied to known vulnerabilities.',
    });
  }

  return improvements;
};

function SecurityInsights() {
  const [vendors, setVendors] = useState([]);
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedVendors, setExpandedVendors] = useState(false);
  const [schedulingOutsideIn, setSchedulingOutsideIn] = useState(false);
  const [outsideInMessage, setOutsideInMessage] = useState('');
  const [outsideInError, setOutsideInError] = useState('');
  const [showLastScanDetails, setShowLastScanDetails] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const { vendors: vendorsData, overview: overviewData } = await getSecurityInsightsData();
        // Sort vendors by effective_penalty in descending order
        const sorted = vendorsData.sort((a, b) => {
          const penaltyA = a.effective_penalty ?? 0;
          const penaltyB = b.effective_penalty ?? 0;
          return penaltyB - penaltyA;
        });
        setVendors(sorted);
        setOverview(overviewData);
      } catch (err) {
        console.error('Failed to fetch vendors:', err);
        setVendors([]);
        setOverview(null);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    if (!overview?.has_pending_outside_in_scan) return undefined;

    const intervalId = setInterval(async () => {
      try {
        const { overview: refreshedOverview } = await getSecurityInsightsData();
        setOverview(refreshedOverview);
      } catch (_err) {
        // Best-effort refresh while waiting for the outside-in scan to complete.
      }
    }, 15000);

    return () => clearInterval(intervalId);
  }, [overview?.has_pending_outside_in_scan]);

  useEffect(() => {
    const outsideInScan = overview?.latest_outside_in_scan || null;

    if (!outsideInScan?.scanned_at) {
      return;
    }

    const outsideInScanId = outsideInScan.id || outsideInScan.scan_id || outsideInScan.scanned_at || 'available';
    trackGoogleAdsConversion('first_scan_completed', {
      once: true,
      onceKey: `first_scan_completed_${outsideInScanId}`,
    });
  }, [overview?.latest_outside_in_scan]);

  // Calculate potential score improvement for the highest-penalty vendor tier
  const calculateScoreImprovement = (vendorsList) => {
    if (!overview || !vendorsList || vendorsList.length === 0) return null;

    const penaltyVendorNetwork = overview.penalty_vendor_network ?? 0;
    const penaltyPolicy = overview.penalty_policy ?? 0;
    const penaltyOutside = overview.penalty_outside_in ?? 0;

    const highestPenalty = Math.max(...vendorsList.map((vendor) => vendor.effective_penalty ?? 0));

    const nextLowerPenalty = vendorsList
      .map((vendor) => vendor.effective_penalty ?? 0)
      .filter((penalty) => penalty < highestPenalty)
      .sort((a, b) => b - a)[0] ?? 0;

    const maxOther = Math.max(penaltyPolicy, penaltyOutside);

    if (penaltyVendorNetwork > maxOther) {
      return {
        improvement: penaltyVendorNetwork - Math.max(nextLowerPenalty, maxOther),
        highestPenalty
      };
    }

    return {
      improvement: null,
      highestPenalty
    };
  };

  // Calculate policy and external integrity compliance status
  const getPolicyComplianceStatus = () => {
    if (!overview) return null;

    const penaltyPolicy = overview.penalty_policy ?? 0;
    const penaltyOutside = overview.penalty_outside_in ?? 0;
    const highestVendorPenalty = Math.max(...vendors.map((vendor) => vendor.effective_penalty ?? 0), 0);

    const issues = [];

    const policyBaseline = Math.max(penaltyOutside, highestVendorPenalty);
    const policyGain = penaltyPolicy - policyBaseline;
    if (policyGain > 0) {
      issues.push({
        type: 'policy',
        gain: policyGain
      });
    }

    const externalBaseline = Math.max(penaltyPolicy, highestVendorPenalty);
    const externalGain = penaltyOutside - externalBaseline;
    if (externalGain > 0) {
      issues.push({
        type: 'external',
        gain: externalGain
      });
    }

    return {
      hasIssues: issues.length > 0,
      issues
    };
  };

  const handleScheduleOutsideIn = async (event) => {
    event.preventDefault();

    if (overview?.has_pending_outside_in_scan) {
      setOutsideInMessage('You are scheduled for a scan soon.');
      return;
    }

    setSchedulingOutsideIn(true);
    setOutsideInError('');
    setOutsideInMessage('');

    try {
      const scan = await scheduleOutsideInScan();
      if (scan.already_scheduled) {
        setOutsideInMessage('You are scheduled for a scan soon.');
      } else {
        setOutsideInMessage('Outside-in scan scheduled. Results will appear after processing.');
      }

      setOverview((previous) => ({
        ...(previous || {}),
        has_pending_outside_in_scan: true,
      }));
    } catch (err) {
      const backendMessage = err?.response?.data?.error;
      setOutsideInError(backendMessage || 'Failed to schedule outside-in scan');
    } finally {
      setSchedulingOutsideIn(false);
    }
  };

  if (loading) {
    return <div className="loading-spinner">Loading security insights...</div>;
  }

  const companySccId = overview?.scc_id ?? null;
  const hasPendingOutsideInScan = Boolean(overview?.has_pending_outside_in_scan);
  const hasStaleScoreData = Boolean(overview?.score_data_stale);
  const staleScoreSinceLabel = formatScanTimestamp(overview?.score_data_stale_since);
  const vendorsInCycle = companySccId == null
    ? []
    : vendors.filter((vendor) => vendor.scc_id === companySccId);
  const hasRiskLoop = companySccId != null && vendorsInCycle.length > 0;
  const sortedCycleVendors = [...vendorsInCycle].sort(
    (a, b) => (b.effective_penalty ?? 0) - (a.effective_penalty ?? 0)
  );
  const cyclePriorityVendors = sortedCycleVendors.slice(0, 2);
  const cyclePriorityNames = cyclePriorityVendors.map((vendor) => vendor.vendor_name).join(' and ');
  const cycleVendorNames = sortedCycleVendors.map((vendor) => vendor.vendor_name).join(', ');
  const loopPenaltyVendorNetwork = overview?.penalty_vendor_network ?? 0;
  const loopNetworkScore = Math.max(0, 100 - loopPenaltyVendorNetwork);
  const highestCycleEffectivePenalty = sortedCycleVendors[0]?.effective_penalty ?? 0;
  const loopPenaltyDifference = Math.max(0, highestCycleEffectivePenalty - loopPenaltyVendorNetwork);
  const organizationName = overview?.name || 'Your organization';
  const loopVendorsPreview = vendorsInCycle.slice(0, 3);
  const loopVendorOverflow = Math.max(vendorsInCycle.length - loopVendorsPreview.length, 0);
  const outsideInScan = overview?.latest_outside_in_scan || null;
  const outsideInImprovements = getExternalIntegrityImprovements(outsideInScan);
  const outsideInScannedAtLabel = formatScanTimestamp(outsideInScan?.scanned_at);
  const parsedScanPayload = parseScanPayload(outsideInScan?.scan_payload);
  const hasScanPayloadDetails = Boolean(parsedScanPayload && typeof parsedScanPayload === 'object');

  const scanTarget = parsedScanPayload?.scan_target || null;
  const scanScore = parsedScanPayload?.score || null;
  const scoreComponents = scanScore?.components || null;
  const network = parsedScanPayload?.network || null;
  const website = parsedScanPayload?.website || null;
  const transport = parsedScanPayload?.transport || null;
  const email = parsedScanPayload?.email || null;
  const dns = parsedScanPayload?.dns || null;
  const cves = parsedScanPayload?.cves || null;
  const payloadScannedAtLabel = formatScanTimestamp(parsedScanPayload?.scanned_at);
  const networkErrorEntries = network?.errors && typeof network.errors === 'object'
    ? Object.entries(network.errors)
    : [];

  const websiteHeaderEntries = website?.headers && typeof website.headers === 'object'
    ? Object.entries(website.headers)
    : [];

  const renderStatusPill = (label, isGood) => (
    <span className={`scan-status-pill ${isGood ? 'is-good' : 'is-risk'}`}>{label}</span>
  );

  return (
    <div className="dashboard-page security-insights">
      <h1>Security Insights</h1>

      {hasStaleScoreData && (
        <div className="stale-data-notice" role="status">
          <span className="material-symbols-outlined">schedule</span>
          <div>
            <p className="stale-data-title">Scores are being recalculated</p>
            <p className="stale-data-text">
              Vendor changes were detected and score-driven recommendations are temporarily hidden until recalculation is complete.
              {staleScoreSinceLabel ? ` Pending since ${staleScoreSinceLabel}.` : ''}
            </p>
          </div>
        </div>
      )}
      
      <div className="insights-container">
        {!hasStaleScoreData && (
          <section className="insights-section risk-loops-section">
            <h2>Risk Loops</h2>
            {hasRiskLoop ? (
              <div className="risk-loops-alert">
                <div className="risk-loops-header">
                  <span className="material-symbols-outlined">warning</span>
                  <div>
                    <p className="risk-loops-title">Risk loop detected</p>
                    <p className="risk-loops-subtitle">
                      Your organization is part of a cycle that limits your score.
                    </p>
                  </div>
                </div>

                <div className="risk-loop-visual">
                  <svg
                    viewBox="0 0 520 220"
                    preserveAspectRatio="xMidYMid meet"
                    aria-hidden="true"
                  >
                    <defs>
                      <linearGradient id="fadeOut" x1="0" y1="0" x2="1" y2="0">
                        <stop offset="0%" stopColor="rgba(255,255,255,0.35)" />
                        <stop offset="60%" stopColor="rgba(255,255,255,0.12)" />
                        <stop offset="100%" stopColor="rgba(255,255,255,0)" />
                      </linearGradient>
                      <linearGradient id="fadeOutDiagLeft" x1="160" y1="60" x2="30" y2="0" gradientUnits="userSpaceOnUse">
                        <stop offset="0%" stopColor="rgba(255,255,255,0.35)" />
                        <stop offset="70%" stopColor="rgba(255,255,255,0.12)" />
                        <stop offset="100%" stopColor="rgba(255,255,255,0)" />
                      </linearGradient>
                      <linearGradient id="fadeOutDiagRight" x1="360" y1="60" x2="500" y2="0" gradientUnits="userSpaceOnUse">
                        <stop offset="0%" stopColor="rgba(255,255,255,0.35)" />
                        <stop offset="70%" stopColor="rgba(255,255,255,0.12)" />
                        <stop offset="100%" stopColor="rgba(255,255,255,0)" />
                      </linearGradient>
                    </defs>

                    {(() => {
                      const leftVendor = loopVendorsPreview[0];
                      const rightVendor = loopVendorsPreview[1];
                      const youX = 260;
                      const youY = 170;
                      const leftX = 130;
                      const leftY = 110;
                      const rightX = 390;
                      const rightY = 110;

                      // For single vendor, show bidirectional cycle between vendor and you
                      if (leftVendor && !rightVendor) {
                        return (
                          <g>
                            <circle cx={youX} cy={youY} r={30} className="loop-node-circle loop-node-company" />
                            <text x={youX} y={youY + 4} textAnchor="middle" className="loop-node-label">You</text>

                            {/* Two vendors on left and right to create triangular balance */}
                            <g>
                              {/* Left: Actual vendor */}
                              <line x1={leftX} y1={leftY} x2={youX - 20} y2={youY - 15} className="loop-line" />
                              <circle cx={leftX} cy={leftY} r={22} className="loop-node-circle loop-node-vendor" />
                              <text x={leftX} y={leftY + 4} textAnchor="middle" className="loop-node-label">V</text>
                              <line x1={leftX - 6} y1={leftY - 6} x2={20} y2={0} stroke="url(#fadeOutDiagLeft)" strokeWidth="2" />
                            </g>

                            {/* Right: Return path from you back to vendor (dashed) */}
                            <g>
                              <line x1={youX + 20} y1={youY - 15} x2={rightX} y2={rightY} className="loop-line" strokeDasharray="4,4" />
                              <circle cx={rightX} cy={rightY} r={22} className="loop-node-circle loop-node-vendor" style={{ opacity: 0.3 }} />
                              <text x={rightX} y={rightY + 4} textAnchor="middle" className="loop-node-label" style={{ opacity: 0.3 }}>V</text>
                              <line x1={rightX + 6} y1={rightY - 6} x2={500} y2={0} stroke="url(#fadeOutDiagRight)" strokeWidth="2" />
                            </g>
                          </g>
                        );
                      }

                      // Two vendors layout
                      return (
                        <g>
                          <circle cx={youX} cy={youY} r={30} className="loop-node-circle loop-node-company" />
                          <text x={youX} y={youY + 4} textAnchor="middle" className="loop-node-label">You</text>

                          {leftVendor && (
                            <g>
                              <line x1={leftX} y1={leftY} x2={youX} y2={youY} className="loop-line" />
                              <circle cx={leftX} cy={leftY} r={22} className="loop-node-circle loop-node-vendor" />
                              <text x={leftX} y={leftY + 4} textAnchor="middle" className="loop-node-label">V</text>
                              <line x1={leftX - 6} y1={leftY - 6} x2={20} y2={0} stroke="url(#fadeOutDiagLeft)" strokeWidth="2" />
                            </g>
                          )}

                          {rightVendor && (
                            <g>
                              <line x1={rightX} y1={rightY} x2={youX} y2={youY} className="loop-line" />
                              <circle cx={rightX} cy={rightY} r={22} className="loop-node-circle loop-node-vendor" />
                              <text x={rightX} y={rightY + 4} textAnchor="middle" className="loop-node-label">V</text>
                              <line x1={rightX + 6} y1={rightY - 6} x2={500} y2={0} stroke="url(#fadeOutDiagRight)" strokeWidth="2" />
                            </g>
                          )}
                        </g>
                      );
                    })()}
                  </svg>

                  <div className="risk-loop-legend">
                    <div className="legend-item">
                      <span className="legend-dot legend-company"></span>
                      <span>Your organization</span>
                    </div>
                    <div className="legend-item">
                      <span className="legend-dot legend-vendor"></span>
                      <span>Vendors in the same cycle</span>
                    </div>
                    <div className="legend-item legend-fade">
                      <span className="legend-fade-line"></span>
                      <span>Downstream network hidden</span>
                    </div>
                  </div>
                </div>

                <div className="risk-loop-vendors">
                  <p className="risk-loop-vendors-title">Direct vendors in this cycle:</p>
                  <div className="risk-loop-vendor-list">
                    {loopVendorsPreview.map((vendor) => (
                      <span key={vendor.id} className="risk-loop-vendor-chip">
                        {vendor.vendor_name}
                      </span>
                    ))}
                    {loopVendorOverflow > 0 && (
                      <span className="risk-loop-vendor-chip">+{loopVendorOverflow} more</span>
                    )}
                  </div>
                </div>

                <div className="risk-loop-priority" role="note" aria-live="polite">
                  <p className="risk-loop-priority-kicker">Priority</p>
                  <p className="risk-loop-priority-title">
                    Focus first on {cyclePriorityNames || 'the highest-risk vendors in this loop'}
                  </p>
                  <p className="risk-loop-priority-text">
                    {organizationName} is currently in a risk loop with {cycleVendorNames || 'your loop vendors'}. Your shared loop network penalty is <strong>-{loopPenaltyVendorNetwork}</strong>, which sets the loop network score at <strong>{loopNetworkScore}</strong> and contributes to your current final score of <strong>{overview?.final_score ?? 'N/A'}</strong>.
                  </p>
                  <p className="risk-loop-priority-text">
                    Highest relationship signal in this loop right now is <strong>-{highestCycleEffectivePenalty}</strong>{sortedCycleVendors[0]?.vendor_name ? ` from ${sortedCycleVendors[0].vendor_name}` : ''}. In loop mode, members share the same applied network penalty (<strong>-{loopPenaltyVendorNetwork}</strong>) for score calculation{loopPenaltyDifference > 0 ? `, so that relationship-level signal is currently ${loopPenaltyDifference} points above the shared applied penalty.` : '.'}
                  </p>
                  <p className="risk-loop-priority-text">
                    Practical next step: prioritize remediation conversations and control improvements with {cyclePriorityNames || 'these vendors'} first, then re-run scoring to confirm the loop penalty drops.
                  </p>
                </div>
              </div>
            ) : (
              <div className="risk-loops-content">
                <span className="material-symbols-outlined" style={{ fontSize: '48px', color: '#00c853', marginBottom: '16px' }}>check_circle</span>
                <p style={{ fontSize: '16px', color: '#00c853', fontWeight: '500' }}>No risk loop identified for your organization</p>
              </div>
            )}
          </section>
        )}

        {!hasStaleScoreData && (
          <>
            {/* Risk Driving Vendors Section */}
            <section className="insights-section risk-vendors-section">
              <h2>Risk Driving Vendors</h2>
              {vendors.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px', opacity: 0.6 }}>
                  <p>No vendors data available</p>
                </div>
              ) : (
                <>
                  {/* Filter vendors with penalty > 0 */}
                  {(() => {
                    const vendorsWithPenalty = vendors.filter(v => (v.effective_penalty ?? 0) > 0);
                    const displayedVendors = expandedVendors ? vendorsWithPenalty : vendorsWithPenalty.slice(0, 5);
                    const scoreImprovementData = calculateScoreImprovement(vendorsWithPenalty);
                    const scoreImprovement = scoreImprovementData?.improvement;
                    const highestPenalty = scoreImprovementData?.highestPenalty;
                    
                    return (
                      <div>
                        <div className="vendors-list">
                          {displayedVendors.map((vendor) => {
                            const isWorstPenaltyVendor = (vendor.effective_penalty ?? 0) === highestPenalty;
                            return (
                              <div key={vendor.id} className="risk-vendor-item">
                                <div className="vendor-name-penalty">
                                  <h3>{vendor.vendor_name}</h3>
                                  {isWorstPenaltyVendor && scoreImprovement > 0 && !hasRiskLoop && (
                                    <div className="vendor-score-improvement">
                                      <span className="material-symbols-outlined">trending_up</span>
                                      <span>Removing this vendor can add +{scoreImprovement} points to your score</span>
                                    </div>
                                  )}
                                  <div className="penalty-badge">
                                    <span className="penalty-value">- {vendor.effective_penalty ?? 0}</span>
                                    <span className="penalty-label">score</span>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        {vendorsWithPenalty.length > 5 && (
                          <button 
                            className="expand-button"
                            onClick={() => setExpandedVendors(!expandedVendors)}
                          >
                            <span className="material-symbols-outlined">
                              {expandedVendors ? 'expand_less' : 'expand_more'}
                            </span>
                            <span>{expandedVendors ? 'Show Less' : `Show All (${vendorsWithPenalty.length})`}</span>
                          </button>
                        )}
                      </div>
                    );
                  })()}
                </>
              )}
            </section>

            {/* External Integrity and Policy Compliance Section */}
            <section className="insights-section policy-compliance-section">
              <h2>External Integrity & Policy Compliance</h2>
              {(() => {
                const complianceStatus = getPolicyComplianceStatus();
                
                if (!complianceStatus?.hasIssues) {
                  const highestVendorPenalty = Math.max(...vendors.map((vendor) => vendor.effective_penalty ?? 0), 0);
                  const noActionMessage = hasRiskLoop
                    ? 'Risk loops currently have higher priority.'
                    : highestVendorPenalty > 0
                      ? 'Risk driving vendors currently have higher priority.'
                      : 'Other actions may have higher priority.';

                  return (
                    <div style={{ textAlign: 'center', padding: '24px', opacity: 0.7 }}>
                      <span className="material-symbols-outlined" style={{ fontSize: '32px', color: '#00c853', marginBottom: '8px', display: 'block' }}>check_circle</span>
                      <p style={{ fontSize: '14px', color: '#00c853' }}>{noActionMessage}</p>
                    </div>
                  );
                }

                return (
                  <div className="policy-issues-list">
                    {complianceStatus.issues.map((issue, idx) => {
                      const label = issue.type === 'policy' ? 'Policy Maturity' : 'External Integrity';
                      const sectionReference = issue.type === 'policy'
                        ? 'policy maturity'
                        : 'External Integrity Improvements';
                      return (
                        <div key={idx} className="policy-issue-item">
                          <span className="material-symbols-outlined" style={{ color: '#ff9800' }}>info</span>
                          <div className="policy-issue-content">
                            <p className="policy-issue-text">
                              Improvements to <strong>{label}</strong> would yield a higher final score by <strong>+{issue.gain}</strong> points. See section about {sectionReference}.
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </section>

            <section className="insights-section improvements-section">
              <h2>External Integrity Improvements</h2>
              <p className="verification-scan-description" style={{ marginBottom: '10px' }}>
                Schedule a fresh outside-in scan to refresh these technical requirements.
              </p>
              {hasPendingOutsideInScan && (
                <div className="verification-scan-pending-notice" style={{ marginBottom: '12px' }}>
                  You are scheduled for a scan soon.
                </div>
              )}
              <form className="verification-scan-form" onSubmit={handleScheduleOutsideIn} style={{ marginBottom: '14px' }}>
                <button
                  type="submit"
                  className="verification-scan-button"
                  disabled={schedulingOutsideIn || hasPendingOutsideInScan}
                >
                  {hasPendingOutsideInScan
                    ? 'Scan already scheduled'
                    : schedulingOutsideIn
                      ? 'Scheduling...'
                      : 'Schedule outside-in scan'}
                </button>
              </form>
              {outsideInMessage && <p className="verification-scan-success">{outsideInMessage}</p>}
              {outsideInError && <p className="verification-scan-error">{outsideInError}</p>}

              {!outsideInScan ? (
                <div style={{ textAlign: 'center', padding: '24px', opacity: 0.7 }}>
                  <span className="material-symbols-outlined" style={{ fontSize: '32px', color: '#9ca3af', marginBottom: '8px', display: 'block' }}>hourglass_empty</span>
                  <p style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
                    No outside-in scan results available yet.
                  </p>
                </div>
              ) : outsideInImprovements.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '24px', opacity: 0.7 }}>
                  <span className="material-symbols-outlined" style={{ fontSize: '32px', color: '#00c853', marginBottom: '8px', display: 'block' }}>check_circle</span>
                  <p style={{ fontSize: '14px', color: '#00c853' }}>
                    No external integrity issues detected in the latest scan.
                  </p>
                  {outsideInScannedAtLabel && (
                    <p style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '6px' }}>
                      Latest scan: {outsideInScannedAtLabel}
                    </p>
                  )}
                </div>
              ) : (
                <>
                  {outsideInScannedAtLabel && (
                    <p style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '12px' }}>
                      Latest scan: {outsideInScannedAtLabel}
                    </p>
                  )}
                  <div className="improvements-list">
                    {outsideInImprovements.map((item, idx) => (
                      <div key={`${item.title}-${idx}`} className="improvement-item">
                        <span className="improvement-icon">⚠️</span>
                        <div className="improvement-content">
                          <h4>{item.title}</h4>
                          <p>{item.recommendation}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {outsideInScan && (
                <div className="scan-details-wrap">
                  <button
                    type="button"
                    className="scan-details-toggle"
                    onClick={() => setShowLastScanDetails((prev) => !prev)}
                    aria-expanded={showLastScanDetails}
                  >
                    <span className="material-symbols-outlined" aria-hidden="true">
                      {showLastScanDetails ? 'expand_less' : 'expand_more'}
                    </span>
                    {showLastScanDetails ? 'Hide last scan details' : 'View last scan details'}
                  </button>

                  {showLastScanDetails && (
                    <div className="scan-details-panel">
                      {!hasScanPayloadDetails ? (
                        <p className="scan-details-empty">
                          Detailed scan payload is not available for this scan.
                        </p>
                      ) : (
                        <>
                          <div className="scan-details-grid">
                            <div className="scan-detail-card">
                              <h4>Scan Target</h4>
                              <p><strong>Company:</strong> {scanTarget?.company_name || overview?.name || 'Unknown'}</p>
                              <p><strong>Domain:</strong> {scanTarget?.domain || overview?.domain || 'Unknown'}</p>
                              <p><strong>Scanned at:</strong> {payloadScannedAtLabel || outsideInScannedAtLabel || 'Unknown'}</p>
                            </div>

                            <div className="scan-detail-card">
                              <h4>Score Breakdown</h4>
                              <p><strong>Raw score:</strong> {scanScore?.raw_score ?? outsideInScan?.raw_score ?? 'N/A'}</p>
                              <p><strong>Penalty:</strong> {scanScore?.penalty ?? outsideInScan?.penalty ?? 'N/A'}</p>
                              {scoreComponents && (
                                <ul className="scan-inline-list">
                                  {Object.entries(scoreComponents).map(([key, value]) => (
                                    <li key={key}><strong>{toTitleCase(key)}:</strong> {value}</li>
                                  ))}
                                </ul>
                              )}
                            </div>

                            <div className="scan-detail-card">
                              <h4>Network</h4>
                              <p><strong>Open ports:</strong> {formatPortList(network?.open_ports)}</p>
                              <p><strong>Risky open ports:</strong> {formatPortList(network?.risky_open_ports)}</p>
                              {networkErrorEntries.length > 0 && (
                                <p><strong>Timeouts / errors:</strong> {networkErrorEntries.map(([port]) => port).join(', ')}</p>
                              )}
                            </div>

                            <div className="scan-detail-card">
                              <h4>Website Security</h4>
                              <p>
                                <strong>Reachable:</strong> {website?.reachable ? 'Yes' : 'No'}
                                {' '}
                                {renderStatusPill(website?.reachable ? 'Up' : 'Down', Boolean(website?.reachable))}
                              </p>
                              <p>
                                <strong>HTTPS redirect:</strong> {website?.http_redirects_to_https ? 'Enabled' : 'Missing'}
                              </p>
                              <p>
                                <strong>Secure headers:</strong> {website?.secure_headers_present ? 'Present' : 'Missing'}
                              </p>
                              <p>
                                <strong>Server info leakage:</strong> {website?.server_leaks_information ? 'Detected' : 'Not detected'}
                              </p>
                              {websiteHeaderEntries.length > 0 && (
                                <ul className="scan-inline-list">
                                  {websiteHeaderEntries.map(([header, enabled]) => (
                                    <li key={header}>
                                      <strong>{toTitleCase(header)}:</strong> {enabled ? 'OK' : 'Missing'}
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </div>

                            <div className="scan-detail-card">
                              <h4>TLS / Transport</h4>
                              <p>
                                <strong>TLS:</strong> {transport?.tls_enabled ? 'Enabled' : 'Not enabled'}
                                {' '}
                                {renderStatusPill(transport?.tls_enabled ? 'Enabled' : 'Disabled', Boolean(transport?.tls_enabled))}
                              </p>
                              <p><strong>Version:</strong> {transport?.tls_version || outsideInScan?.tls_version || 'Unknown'}</p>
                              <p><strong>Security:</strong> {transport?.security || 'Unknown'}</p>
                              <p><strong>Certificate trusted:</strong> {transport?.trusted_certificate ? 'Yes' : 'No'}</p>
                              <p><strong>Expires in:</strong> {transport?.expires_in_days ?? 'N/A'} days</p>
                            </div>

                            <div className="scan-detail-card">
                              <h4>Email / DNS / CVE</h4>
                              <p><strong>SPF:</strong> {email?.spf_valid ? 'Valid' : 'Invalid or missing'}</p>
                              <p><strong>DMARC:</strong> {email?.dmarc_valid ? 'Valid' : 'Invalid or missing'}</p>
                              <p><strong>DMARC policy:</strong> {email?.dmarc_policy || 'N/A'}</p>
                              <p><strong>DNSSEC:</strong> {dns?.dnssec_enabled ? 'Enabled' : 'Disabled'}</p>
                              <p><strong>CVE matches:</strong> {Array.isArray(cves?.matches) ? cves.matches.length : (cves?.matches ?? 0)}</p>
                              <p><strong>CVE risk signal:</strong> {cves?.risk_signal ?? outsideInScan?.cve_risk_signal ?? 0}</p>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              )}
            </section>
          </>
        )}
      </div>
    </div>
  );
}

export default SecurityInsights;
